import type { RowData } from "@tanstack/react-table";
import type { DataGridTable } from "../../columns/types";
import {
    type CellRect,
    getActiveRect,
    getColumnIndex,
    hasMultiCellRange,
    selectRect,
} from "../../editing/cellRect";
import type { GridDirection } from "../../focus/keyMap";
import {
    getFocusedAddress,
    getNavigableColumnIds,
} from "../../focus/navigation";

const getColumnSpan = <TData extends RowData>(table: DataGridTable<TData>) => {
    const columnIds = getNavigableColumnIds(table);
    const firstColumnId = columnIds[0];
    const lastColumnId = columnIds[columnIds.length - 1];
    return firstColumnId && lastColumnId
        ? {
              left: getColumnIndex(table, firstColumnId),
              right: getColumnIndex(table, lastColumnId),
          }
        : null;
};

const getRowSpan = <TData extends RowData>(table: DataGridTable<TData>) => {
    const rowCount = table.getRowsInDisplayOrder().length;
    return rowCount > 0 ? { top: 0, bottom: rowCount - 1 } : null;
};

const select = <TData extends RowData>(
    table: DataGridTable<TData>,
    rect: CellRect | null,
) => rect !== null && selectRect({ table, rect });

const STEPS: Record<GridDirection, { axis: "row" | "column"; step: number }> = {
    up: { axis: "row", step: -1 },
    down: { axis: "row", step: 1 },
    left: { axis: "column", step: -1 },
    right: { axis: "column", step: 1 },
};

/**
 * Moves the edge that doesn't hold the active cell one step, as Excel does:
 * with the active cell on neither edge, the edge the arrow points at.
 */
export const moveFarEdge = ({
    start,
    end,
    active,
    step,
    min,
    max,
}: {
    start: number;
    end: number;
    active: number;
    step: number;
    min: number;
    max: number;
}) => {
    const isEndFar = active === start || (active !== end && step > 0);
    const moved = Math.min(Math.max((isEndFar ? end : start) + step, min), max);
    const fixed = isEndFar ? start : end;
    return { start: Math.min(fixed, moved), end: Math.max(fixed, moved) };
};

/**
 * Shift+arrow. A range built by `selectRect` (Shift+Space, Mod+Space, Mod+A)
 * is two ranges, so it grows here as one rectangle and keeps the active cell
 * where it is; any other range grows through TanStack.
 */
export const extendRange = <TData extends RowData>(
    table: DataGridTable<TData>,
    direction: GridDirection,
) => {
    const ranges = table.atoms.cellSelection.get();
    const active = getFocusedAddress(ranges);
    const rect = getActiveRect(table);
    const rows = getRowSpan(table);
    const columns = getColumnSpan(table);
    const activeRow =
        active && table.getPrePaginatedRowModel().rowsById[active.rowId];
    if (
        ranges.length < 2 ||
        !active ||
        !activeRow ||
        !rect ||
        !rows ||
        !columns
    ) {
        table.extendCellSelection(direction);
        return true;
    }
    const { axis, step } = STEPS[direction];
    if (axis === "row") {
        const edges = moveFarEdge({
            start: rect.top,
            end: rect.bottom,
            active: activeRow.getDisplayIndex(),
            step,
            min: rows.top,
            max: rows.bottom,
        });
        return select(table, { ...rect, top: edges.start, bottom: edges.end });
    }
    const edges = moveFarEdge({
        start: rect.left,
        end: rect.right,
        active: getColumnIndex(table, active.columnId),
        step,
        min: columns.left,
        max: columns.right,
    });
    return select(table, { ...rect, left: edges.start, right: edges.end });
};

/** Escape with a range: back to just the active cell. */
export const collapseRange = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const active = getFocusedAddress(table.atoms.cellSelection.get());
    if (!active || !hasMultiCellRange(table)) {
        return false;
    }
    table.setFocusedCell(active.rowId, active.columnId);
    return true;
};

/** Shift+Space: the range's rows across every selectable column. */
export const selectRangeRows = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const rect = getActiveRect(table);
    const columns = getColumnSpan(table);
    return select(table, rect && columns ? { ...rect, ...columns } : null);
};

/** Mod+Space: the range's columns across every row. */
export const selectRangeColumns = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const rect = getActiveRect(table);
    const rows = getRowSpan(table);
    return select(table, rect && rows ? { ...rect, ...rows } : null);
};

/** Mod+A: every cell, with the active cell left where it is. */
export const selectAllRange = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const rows = getRowSpan(table);
    const columns = getColumnSpan(table);
    return select(table, rows && columns ? { ...rows, ...columns } : null);
};
